import { useState, useEffect } from "react";
import { API , authFetch } from "@/App";
import {
  Globe, Phone, Mail, MapPin, Store, User, Clock, CheckCircle, AlertCircle, Copy
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const WebhooksPage = () => {
  const [leads, setLeads] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("leads");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [leadsRes, inquiriesRes] = await Promise.all([
          authFetch(`${API}/webhooks/leads`, {  }),
          authFetch(`${API}/webhooks/dealer-inquiries`, {  }),
        ]);
        const leadsData = await leadsRes.json();
        const inquiriesData = await inquiriesRes.json();
        if (leadsData.success) setLeads(leadsData.data || []);
        if (inquiriesData.success) setInquiries(inquiriesData.data || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const webhookUrl = `${API}/webhooks`;
  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard");
  };

  return (
    <div className="space-y-6" data-testid="webhooks-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Website Integration</h1>
        <p className="text-sm text-slate-500 mt-1">Webhook endpoints for avicement.com and lead management</p>
      </div>

      {/* Webhook URLs */}
      <Card className="p-5" data-testid="webhook-urls">
        <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">Webhook Endpoints</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
            <div>
              <p className="text-xs font-semibold text-slate-700">Lead/Inquiry Submission</p>
              <code className="text-[10px] text-slate-500 font-mono block mt-0.5">{webhookUrl}/lead</code>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">POST</Badge>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyUrl(`${webhookUrl}/lead`)} data-testid="copy-lead-url">
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
            <div>
              <p className="text-xs font-semibold text-slate-700">Dealer Application</p>
              <code className="text-[10px] text-slate-500 font-mono block mt-0.5">{webhookUrl}/dealer-inquiry</code>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">POST</Badge>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyUrl(`${webhookUrl}/dealer-inquiry`)} data-testid="copy-dealer-url">
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-3 p-3 bg-blue-50 rounded-lg text-[10px] text-blue-700">
          <p className="font-semibold">Integration Guide</p>
          <p className="mt-1">Send POST requests with JSON body containing: name, email, phone, message, source, region, product_interest</p>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Total Leads</p>
          <p className="text-2xl font-bold font-mono text-slate-900 mt-1">{leads.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">New Leads</p>
          <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">{leads.filter(l => l.status === "NEW").length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Dealer Inquiries</p>
          <p className="text-2xl font-bold font-mono text-amber-600 mt-1">{inquiries.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Pending Inquiries</p>
          <p className="text-2xl font-bold font-mono text-red-600 mt-1">{inquiries.filter(i => i.status === "PENDING").length}</p>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-[#8B1A1A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="leads" data-testid="tab-leads">
              <User className="w-3.5 h-3.5 mr-1" /> Leads ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="dealer-inquiries" data-testid="tab-dealer-inquiries">
              <Store className="w-3.5 h-3.5 mr-1" /> Dealer Inquiries ({inquiries.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            {leads.length === 0 ? (
              <Card className="p-6 text-center text-slate-400"><Globe className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">No leads yet. Integrate the webhook with your website.</p></Card>
            ) : (
              <div className="space-y-3">
                {leads.map(lead => (
                  <Card key={lead.lead_id} className="p-4" data-testid={`lead-${lead.lead_id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{lead.name || "Anonymous"}</span>
                          <Badge variant="outline" className="text-[10px]">{lead.source || "website"}</Badge>
                          <Badge className={lead.status === "NEW" ? "bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]" : "text-[10px]"}>{lead.status}</Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                          {lead.email && <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{lead.email}</span>}
                          {lead.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{lead.phone}</span>}
                          {lead.region && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{lead.region}</span>}
                        </div>
                        {lead.message && <p className="text-xs text-slate-600 mt-2">{lead.message}</p>}
                      </div>
                      <span className="text-[9px] text-slate-400 font-mono shrink-0">{lead.created_at?.split("T")[0]}</span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="dealer-inquiries">
            {inquiries.length === 0 ? (
              <Card className="p-6 text-center text-slate-400"><Store className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">No dealer inquiries yet.</p></Card>
            ) : (
              <div className="space-y-3">
                {inquiries.map(inq => (
                  <Card key={inq.inquiry_id} className="p-4" data-testid={`inquiry-${inq.inquiry_id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{inq.firm_name || "Unknown"}</span>
                          <Badge className={inq.status === "PENDING" ? "bg-amber-50 text-amber-700 border border-amber-200 text-[10px]" : "text-[10px]"}>{inq.status}</Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                          <span>{inq.contact_person}</span>
                          {inq.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{inq.phone}</span>}
                          {inq.district && <span>{inq.district}, {inq.state}</span>}
                        </div>
                      </div>
                      <span className="text-[9px] text-slate-400 font-mono shrink-0">{inq.created_at?.split("T")[0]}</span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default WebhooksPage;
